import React, {
  createContext,
  useContext,
  useReducer,
  useRef,
  useEffect,
  useMemo,
  ReactNode,
} from 'react'
import {
  dashboardActions,
  dashboardInitialState,
  dashboardReducer,
  DashboardState,
} from '@/reducer'

type DashboardContextType = {
  state: DashboardState
  actions: typeof dashboardActions,
  id: Number,
  dispatch: any
}

const DashboardContext = createContext<DashboardContextType | undefined>(
  undefined,
)

export const useDashboardContext = (): DashboardContextType => {
  const context = useContext(DashboardContext)
  if (!context) {
    throw new Error(
      'useDashboardContext must be used within a DashboardContextProvider',
    )
  }
  return context
}

type DashboardContextProviderProps = {
  children: ReactNode
}

export const DashboardContextProvider: React.FC<
  DashboardContextProviderProps
> = ({ children }) => {
  const [dashboardState, dispatchDashboard] = useReducer(
    dashboardReducer,
    dashboardInitialState,
  )
  const stateRef = useRef<DashboardState>(dashboardInitialState)

  const id = 1;

  const value = useMemo(
    () => ({
      state: dashboardState,
      actions: dashboardActions, 
      id,
      dispatch: dispatchDashboard
    }),
    [dashboardState, id], 
  )

  useEffect(() => {
    stateRef.current = dashboardState
  }, [dashboardState])

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  )
}
